import React, { useState, useEffect, useRef } from "react";
import { Card } from "react-bootstrap";
import ErrorComponent from "../../../Components/ErrorMessage";
import { replacePlaceholder } from "../../../Utils/Utils";

const SingleDropdownQuestion = ({
  question,
  formValues,
  handleChange,
  showError,
  getChoiceOrder,
  setChoiceOrder,
}) => {
  const isMandatory = question.isMandatory;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sortedChoices, setSortedChoices] = useState([]);
  const questionText = replacePlaceholder(question.questionText, formValues);
  const questionKey = question.tempId;

  /* ---------- RANDOMIZE refs ---------- */
  const seedRef = useRef({});

  const shuffleArrayWithSeed = (array, seed) => {
    const newArray = [...array];
    let random = seed;

    for (let i = newArray.length - 1; i > 0; i--) {
      random = (random * 9301 + 49297) % 233280;
      const j = Math.floor((random / 233280) * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Get current value (UNCHANGED)
  const getCurrentValue = () => {
    const value = formValues[questionKey];
    if (Array.isArray(value)) return value[0] || "";
    return value || "";
  };

  const currentValue = getCurrentValue();
  const hasAnswer = currentValue !== "";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------- RANDOMIZE once + freeze ---------- */
  useEffect(() => {
    // use frozen order if exists
    const frozen = getChoiceOrder?.(questionKey);
    if (frozen) {
      setSortedChoices(frozen);
      return;
    }

    let choicesToRender = [...question.choices];

    // do not randomize if already answered
    if (!hasAnswer) {
      if (question.choicesOrder === "random") {
        if (!seedRef.current[questionKey]) {
          seedRef.current[questionKey] = Math.random();
        }
        choicesToRender = shuffleArrayWithSeed(
          choicesToRender,
          seedRef.current[questionKey]
        );
      } else if (question.choicesOrder === "ascending") {
        choicesToRender.sort((a, b) => a.text.localeCompare(b.text));
      } else if (question.choicesOrder === "descending") {
        choicesToRender.sort((a, b) => b.text.localeCompare(a.text));
      }
    }

    setSortedChoices(choicesToRender);
  }, [questionKey, question.choicesOrder]);

  /* ---------- Freeze on first selection ---------- */
  const handleSelectChange = (e) => {
    if (!getChoiceOrder?.(questionKey) && setChoiceOrder) {
      setChoiceOrder(questionKey, sortedChoices);
    }
    handleChange(e);
  };

  const optionsLength = question.choices.reduce((max, choice) => {
    return choice.text.length > max ? choice.text.length : max;
  }, 0);

  const selectWidth =
    optionsLength < 20 ? (isMobile ? "99%" : "20%") : "80%";
  const optionWidth =
    optionsLength < 20 || isMobile ? (isMobile ? "80%" : "20%") : "80%";

  /* ---------- UI (UNCHANGED) ---------- */
  return (
    <Card
      className={`mt-3 ${
        isMandatory && showError && !hasAnswer ? "border-danger" : ""
      }`}
    >
      <Card.Body>
        <div className="d-flex flex-column align-items-start">
          <div className="d-flex flex-column">
            <b style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              <label htmlFor={questionKey}>
                {questionKey}.{questionText}{" "}
                {isMandatory && <span className="text-danger">*</span>}
              </label>
            </b>

            <b style={{ color: "lightblue" }}>
              {question.questionDescription}
            </b>

            {isMandatory && showError && !hasAnswer && (
              <ErrorComponent message={question.requiredErrorText} />
            )}
          </div>

          <select
            id={questionKey}
            name={questionKey}
            value={currentValue}
            onChange={handleSelectChange}
            className="form-select"
            style={{ width: selectWidth, marginTop: "5px", padding: "10px" }}
          >
            <option value="">Select an option</option>
            {sortedChoices.map((choice, index) => (
              <option
                key={index}
                value={choice.value}
                style={{ width: optionWidth }}
              >
                {choice.text}
              </option>
            ))}
          </select>
        </div>
      </Card.Body>
    </Card>
  );
};


export default SingleDropdownQuestion;
